import React from 'react';
import Alert from '@amzn/meridian/alert';
import Column from '@amzn/meridian/column';
import Responsive from '@amzn/meridian/responsive';
import SearchField from '@amzn/meridian/search-field';
import Text from '@amzn/meridian/text';
import UILoader from 'src/components/common/loader/UILoaderContainer';
import { responsiveHeightProps, responsiveProps } from 'src/constants/responsiveProps';
import { AlertLevel, AlertLevels } from 'src/models/alert-model';
import { ScanResult } from 'src/models/unload';
import { UNKNOWN_ERROR } from 'src/localization/string-ids/error-constants';
import { UNLOAD_NOT_ENABLED_BODY, UNLOAD_NOT_ENABLED_TITLE, UNLOAD_ORPHAN_ENDED_TITLE, UNLOAD_SIDELINED_TITLE }
    from 'src/localization/string-ids/app-strings';
import { primeScanAudio } from 'src/utils/scan-audio';
import { UnloadCallPending } from 'src/components/pages/unload-page/UnloadCallPending';
import './UnloadScanStep.scss';

export interface ScanResultMessage {
    // The alert's level, which sets its colour and icon
    type: AlertLevel;
    // The alert's title
    title: string;
    // The alert's body, one entry a line
    lines: string[];
    // A last line that only repeats what is on screen, so a short screen leaves it out
    detail?: string;
}

// A failed scan reads as in every other mode: the value over the message the call failed with
export const getFailureMessage = (scan: ScanResult<string>): ScanResultMessage =>
    ({ type: AlertLevels.ERROR, title: scan.value, lines: [scan.message ?? UNKNOWN_ERROR] });

// The orphan loop just ended, shown on the step it returned to
export const ORPHAN_ENDED_MESSAGE: ScanResultMessage = { type: AlertLevels.SUCCESS, title: UNLOAD_ORPHAN_ENDED_TITLE,
    lines: [] };

// Unloading is not on at the site, shown on the step whose scan found it
export const NOT_ENABLED_MESSAGE: ScanResultMessage = { type: AlertLevels.INFORMATIONAL,
    title: UNLOAD_NOT_ENABLED_TITLE, lines: [UNLOAD_NOT_ENABLED_BODY] };

// A sideline ID recorded, shown on the loop it returns to
export const getSidelinedMessage = (scan: ScanResult<string>): ScanResultMessage =>
    ({ type: AlertLevels.WARNING, title: UNLOAD_SIDELINED_TITLE, lines: [scan.value] });

interface UnloadScanStepProps {
    // The image above the heading
    icon: string;
    // What the step asks for
    heading: string;
    // A line under the heading, such as a count
    subheading?: string;
    // The scan field's data-testid
    fieldTestId: string;
    // The scan field's label
    fieldLabel: string;
    // The scan field's placeholder
    placeholder?: string;
    // The latest result, shown under the field
    message?: ScanResultMessage;
    // Shown alone where a result would be, while there is none
    prompt?: string;
    // Takes each scan, trimmed, and never an empty one
    onSubmit: (value: string) => void;
}

// A scan begun while a call is out stays ignored to its end, even if the answer lands halfway through it.
// A scan has ended once 100 ms pass with no keystroke, the same gap SearchInput's auto-submit uses
const SCAN_GAP_MS = 100;

// Set while the keystrokes of a scan begun during a call are still coming. The page's rather than one field's, since
// the answer can open another step partway through the scan, and that step's field ignores the rest of it too
let scanGapTimer: ReturnType<typeof setTimeout> | undefined;

// An unload step: the one scan field, with the latest result under it so the field never moves
class UnloadScanStep extends React.Component<UnloadScanStepProps, { value: string }> {
    // Whether a call is in flight, which the unload page sets
    static contextType = UnloadCallPending;
    state = { value: '' };
    private field = React.createRef<HTMLDivElement>();

    // A wedge scanner types wherever the cursor is, so each tap on the page, and each key typed outside the field, puts
    // it back in the field
    public componentDidMount() {
        document.addEventListener('click', this.focusField);
        document.addEventListener('focusin', this.onFocusIn);
        document.addEventListener('keydown', this.onKeyDown, true);
    }

    public componentWillUnmount() {
        document.removeEventListener('click', this.focusField);
        document.removeEventListener('focusin', this.onFocusIn);
        document.removeEventListener('keydown', this.onKeyDown, true);
    }

    // Except in a dialog, such as the side menu, which keeps the cursor inside it while it is open
    private focusField = (event: Event) => {
        if (!(event.target instanceof Element && event.target.closest('[role="dialog"]'))) {
            this.field.current?.querySelector('input')?.focus();
        }
    };

    // Focus can also move, with no click reaching here, onto what is not a control (tabIndex -1), such as the page
    // around the field; this puts the cursor back then too
    private onFocusIn = (event: FocusEvent) => {
        if ((event.target as HTMLElement).tabIndex < 0) {
            this.focusField(event);
        }
    };

    // Focus can also be left on a control, as when the side menu closes and hands it back to its menu button. A scan
    // typed then would be lost and its Enter would press that control, so its first character moves the cursor to the
    // field, which then takes that character. Enter, Tab, Space and Ctrl, Alt or Meta shortcuts still reach the
    // control
    private onKeyDown = (event: KeyboardEvent) => {
        if (/^\S$/.test(event.key) && !event.ctrlKey && !event.altKey && !event.metaKey
            && !(event.target instanceof HTMLInputElement)) {
            this.focusField(event);
        }
    };

    // While a call is in flight the field takes nothing and keeps the cursor. A scan begun then is ignored through its
    // Enter, even if the answer lands partway through it
    private ignoring = () => (this.context as boolean) || scanGapTimer !== undefined;

    // A keystroke is a user gesture, so the tones' audio starts here, ahead of the answer that sounds one
    private onChange = (value: string) => {
        primeScanAudio();
        if (this.ignoring()) {
            clearTimeout(scanGapTimer);
            scanGapTimer = setTimeout(() => { scanGapTimer = undefined; }, SCAN_GAP_MS);
            return;
        }
        this.setState({ value });
    };

    // Enter, which a wedge scanner sends, lands here
    private onSubmit = () => {
        if (this.ignoring()) {
            clearTimeout(scanGapTimer);
            scanGapTimer = undefined;
            this.setState({ value: '' });
            return;
        }
        const value = this.state.value.trim();
        this.setState({ value: '' });
        if (value) {
            this.props.onSubmit(value);
        }
    };

    public render() {
        const { message } = this.props;
        return (
            <Responsive query="max-width" props={responsiveProps}>
                {props => (
                    <Responsive query="max-height" props={responsiveHeightProps}>
                        {heightProps => (
                            <Column className="unload-scan-step" spacing={props.columnSpacing} alignmentHorizontal="center">
                                {/* Only decoration, so short screens drop it to give the result room */}
                                {!heightProps.isShortViewport &&
                                    <img src={this.props.icon} alt="" className="unload-scan-icon"
                                         data-testid="unload-scan-icon"/>
                                }
                                <div data-testid="unload-scan-heading">
                                    <Text type={props.titleSize} alignment="center">{this.props.heading}</Text>
                                    {this.props.subheading && <Text alignment="center">{this.props.subheading}</Text>}
                                </div>
                                <div className="unload-scan-field" data-testid={this.props.fieldTestId} ref={this.field}>
                                    {/* Smaller fields would show the label above the box, costing a line; screen readers still get it */}
                                    <SearchField inaccessibleAutoFocus={true} type="text" value={this.state.value}
                                                 onChange={this.onChange} onSubmit={this.onSubmit}
                                                 size={props.inputBarSize} searchButton={true}
                                                 label={props.inputBarSize === 'xlarge' ? this.props.fieldLabel : undefined}
                                                 aria-label={this.props.fieldLabel} placeholder={this.props.placeholder}/>
                                    {/* The progress bar sits in the gap under the field, so it never covers text or pushes anything down */}
                                    <UILoader/>
                                </div>
                                {/* Rendered only once there is a result, so the field stays put when the first one appears */}
                                {message ?
                                    <div data-testid="unload-message">
                                        {/* alertSize is large at every tier, because smaller alerts hide their title */}
                                        <Alert type={message.type} title={message.title} size={props.alertSize}>
                                            {message.lines.map((line, index) => <div key={index}>{line}</div>)}
                                            {message.detail && !heightProps.isShortViewport && <div>{message.detail}</div>}
                                        </Alert>
                                    </div>
                                    : this.props.prompt &&
                                    <div data-testid="unload-prompt">
                                        <Text alignment="center">{this.props.prompt}</Text>
                                    </div>
                                }
                            </Column>
                        )}
                    </Responsive>
                )}
            </Responsive>
        );
    }
}

export default UnloadScanStep;
